import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ExternalLink } from "lucide-react";
import { useI18n } from "../i18n";
import { pathFor, OFFICIAL_LINKS } from "../config/site";
import { IMAGES } from "../data/structure";

const word = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: 0.15 + i * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Hero() {
  const { lang, d } = useI18n();
  const accentWords = d.hero.titleAccent.split(" ");

  return (
    <section data-testid="hero" className="relative overflow-hidden min-h-[88vh] flex items-center">
      <div className="absolute inset-0">
        <img
          src={IMAGES.stageLights}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover opacity-40"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-jlo-bg/85 via-jlo-bg/70 to-jlo-bg" />
        <img
          src={IMAGES.goldStreaks}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover mix-blend-screen opacity-20 animate-floaty"
          loading="lazy"
        />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="text-xs sm:text-sm uppercase tracking-[0.3em] text-jlo-gold font-semibold mb-6"
        >
          {d.timelineSection.kicker}
        </motion.p>

        <h1 className="font-display font-black leading-[0.95] text-5xl sm:text-6xl lg:text-7xl max-w-4xl">
          <span className="block text-jlo-warm">{d.hero.title}</span>
          <span className="block mt-2">
            {accentWords.map((w, i) => (
              <motion.span
                key={i}
                custom={i}
                variants={word}
                initial="hidden"
                animate="show"
                className="inline-block mr-[0.25em] gold-text"
              >
                {w}
              </motion.span>
            ))}
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.7 }}
          className="mt-8 max-w-2xl text-base sm:text-lg text-jlo-warm/80 leading-relaxed"
        >
          {d.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-3"
        >
          <Link
            to={pathFor(lang, "timeline")}
            data-testid="hero-explore-timeline"
            className="focus-gold group inline-flex items-center gap-2 rounded-full bg-jlo-gold px-6 py-3 text-sm font-semibold text-jlo-bg hover:bg-jlo-warm transition-colors"
          >
            {d.ui.exploreTimeline}
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <a
            href={OFFICIAL_LINKS.website}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-official-website"
            className="focus-gold inline-flex items-center gap-2 rounded-full border border-jlo-gold/40 px-6 py-3 text-sm font-semibold text-jlo-warm hover:border-jlo-gold hover:text-jlo-gold transition-colors"
          >
            {d.ui.officialWebsite}
            <ExternalLink className="h-4 w-4" />
          </a>
          <a
            href={OFFICIAL_LINKS.store}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-official-store"
            className="focus-gold inline-flex items-center gap-2 rounded-full border border-jlo-gold/40 px-6 py-3 text-sm font-semibold text-jlo-warm hover:border-jlo-gold hover:text-jlo-gold transition-colors"
          >
            {d.ui.officialStore}
            <ExternalLink className="h-4 w-4" />
          </a>
          <a
            href={OFFICIAL_LINKS.onthejlo}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-on-the-jlo"
            className="focus-gold inline-flex items-center gap-2 rounded-full border border-jlo-gold/40 px-6 py-3 text-sm font-semibold text-jlo-warm hover:border-jlo-gold hover:text-jlo-gold transition-colors"
          >
            {d.ui.onTheJlo}
            <ExternalLink className="h-4 w-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
